import { useState, useEffect, useCallback } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'
import type { Review } from '../types'

interface UseReviewsOptions {
  page?: number
  limit?: number
  status?: 'all' | 'flagged' | 'reported'
  minRating?: number
  maxRating?: number
}

export function useReviews(client: SupabaseClient | null, options: UseReviewsOptions = {}) {
  const { page = 1, limit = 20, status = 'all', minRating, maxRating } = options

  const [reviews, setReviews] = useState<Review[]>([])
  const [total, setTotal] = useState(0)
  const [stats, setStats] = useState({ total: 0, flagged: 0, reported: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchReviews = useCallback(async () => {
    if (!client) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      let query = client
        .from('product_reviews')
        .select('*', { count: 'exact' })

      // Apply status filter
      if (status === 'flagged') {
        query = query.eq('is_flagged', true)
      } else if (status === 'reported') {
        query = query.gt('report_count', 0)
      }

      // Apply rating filters
      if (minRating !== undefined) {
        query = query.gte('rating', minRating)
      }
      if (maxRating !== undefined) {
        query = query.lte('rating', maxRating)
      }

      // Apply pagination
      const from = (page - 1) * limit
      const to = from + limit - 1
      query = query.range(from, to).order('created_at', { ascending: false })

      const { data, error: queryError, count } = await query

      if (queryError) throw queryError

      setReviews(data || [])
      setTotal(count || 0)

      // Fetch stats
      const [{ count: totalCount }, { count: flaggedCount }, { count: reportedCount }] = await Promise.all([
        client.from('product_reviews').select('*', { count: 'exact', head: true }),
        client.from('product_reviews').select('*', { count: 'exact', head: true }).eq('is_flagged', true),
        client.from('product_reviews').select('*', { count: 'exact', head: true }).gt('report_count', 0),
      ])

      setStats({
        total: totalCount || 0,
        flagged: flaggedCount || 0,
        reported: reportedCount || 0,
      })
    } catch (err: any) {
      console.error('Error fetching reviews:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [client, page, limit, status, minRating, maxRating])

  useEffect(() => {
    fetchReviews()
  }, [fetchReviews])

  const flagReview = useCallback(async (reviewId: string, flagged: boolean) => {
    if (!client) return false

    try {
      const { error: updateError } = await client
        .from('product_reviews')
        .update({ is_flagged: flagged, updated_at: new Date().toISOString() })
        .eq('id', reviewId)

      if (updateError) throw updateError
      await fetchReviews()
      return true
    } catch (err: any) {
      console.error('Error flagging review:', err)
      return false
    }
  }, [client, fetchReviews])

  return { reviews, total, stats, isLoading, error, refetch: fetchReviews, flagReview }
}
